import React, { FC } from 'react';

import Checkbox from '../../components/Checkbox/Checkbox';
import Input from '../../components/Input/Input';
import InputSelectDropdown from '../../components/InputSelectDropdown/InputSelectDropdown';
import { cn } from '../../components/lib/utils';
import RichText from '../../components/RichText/RichText';
import { MarkdownEditorWrapper } from '../../components/RichTextEditor/MarkdownEditorWrapper';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '../../components/ui';
import { ColorPicker } from '../../components/ui/ColorPicker/color-picker';
import UploadImage from '../../components/UploadImage/UploadImage';

import {
  DEFAULT_FONT_SIZE,
  loginPopupFontOptions,
  loginPopupFontWeightOptions
} from './constants';
import { LoginPopupSettingsProps } from './types';

const LoginPopupSettings: FC<LoginPopupSettingsProps> = ({
  control,
  errors,
  watch,
  initialFontSize
}) => {
  return (
    <div className="border border-base-sidebarBorder rounded-[20px] mb-5 p-9">
      <div className="flex gap-y-9 flex-col w-full max-w-[586px]">
        <RichText
          title="Login popup configuration"
          description="Customize your portal's login"
        />
        <FormField
          control={control}
          name="loginPopupLogo"
          render={({ field: { onChange, value } }) => (
            <FormItem>
              <FormControl>
                <UploadImage
                  title="Add a logo"
                  description="Upload or select a logo from the assets library"
                  buttonText="Upload"
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  className={cn(
                    'w-full',
                    errors.loginPopupLogo?.message ? 'mb-0' : ''
                  )}
                  selectedImageUrl={value}
                />
              </FormControl>
              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="loginPopupBackgroundImageDesktop"
          render={({ field: { onChange, value } }) => (
            <FormItem>
              <FormControl>
                <UploadImage
                  title="Popup Background Image"
                  description="Upload or select background image"
                  buttonText="Upload"
                  onUpload={(url: string) => {
                    onChange(url);
                  }}
                  className={cn(
                    'w-full',
                    errors.loginPopupBackgroundImageDesktop?.message
                      ? 'mb-0'
                      : ''
                  )}
                  selectedImageUrl={value}
                />
              </FormControl>
              <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
            </FormItem>
          )}
        />
        <FormField
          name="loginPopupText"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Welcoming Text"
                  placeholder="Text input for custom welcome message"
                  value={value}
                  onChange={onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-9 ">
          <FormField
            name="loginPopupFontColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Font color picker"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="loginPopupFontSize"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    step={2}
                    min={12}
                    type="number"
                    label="Font size"
                    value={value === undefined || value === null ? '' : value}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        onChange('');
                      } else {
                        onChange(Number(val));
                      }
                    }}
                    onBlur={() => {
                      if (
                        typeof value !== 'number' ||
                        isNaN(value) ||
                        value < DEFAULT_FONT_SIZE
                      ) {
                        onChange(initialFontSize);
                      }
                      onBlur?.();
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="loginPopupFontWeight"
            render={({ field: { onChange } }) => (
              <FormItem>
                <FormControl>
                  <InputSelectDropdown
                    label="Font weight"
                    defaultSelected={loginPopupFontWeightOptions.find(
                      (option) => option.value === watch('loginPopupFontWeight')
                    )}
                    options={loginPopupFontWeightOptions}
                    onOptionChange={onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="loginPopupFont"
            render={({ field: { onChange } }) => (
              <FormItem>
                <FormControl>
                  <InputSelectDropdown
                    label="Font family"
                    defaultSelected={loginPopupFontOptions.find(
                      (option) => option.value === watch('loginPopupFont')
                    )}
                    options={loginPopupFontOptions}
                    onOptionChange={onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div>
          <FormField
            name="loginPopupConsentSection.htmlText"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <MarkdownEditorWrapper
                    label="Body text"
                    placeholder="e.g., Write your blog post here. Share exciting updates, game features, behind-the-scenes insights, or special offers."
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="mt-2.5">
            <FormField
              name="loginPopupConsentSection.consentRequired"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Checkbox
                      checked={value}
                      onCheckedChange={onChange}
                      description="Ask for player's active consent before enabling the login methods"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPopupSettings;
